class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Quantity updates mark the persistent drawer empty. Replacing its children
    // after an add does not clear that class, so Dawn keeps the new list hidden.
    const empty = !!this.querySelector('.drawer__inner-empty');
    this.classList.toggle('is-empty', empty);
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', empty);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', empty);
    if (this.classList.contains('active')) {
      const inner = this.querySelector('.drawer__inner');
      trapFocus(inner, this.querySelector('.drawer__close') || inner);
    }

    const success = this.querySelector('[data-cw-success]');
    if (success) success.hidden = !parsedState.id;
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Preserve Dawn and Shiprocket checkout; extend only cart presentation and suggestions.
(() => {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  const bridge = () => {
    const notification = document.querySelector('cart-notification');
    if (!notification) return;
    notification.hidden = true;
    notification.style.setProperty('display', 'none', 'important');
    for (const method of ['getSectionsToRender', 'setActiveElement', 'renderContents', 'open', 'close']) {
      notification[method] = drawer[method].bind(drawer);
    }
  };
  bridge();
  customElements.whenDefined('cart-notification').then(bridge);
  let pending = false;
  drawer.addEventListener('click', event => {
    const arrow = event.target.closest('[data-cw-slide]');
    if (arrow) drawer.querySelector('.cw-suggestions-track')?.scrollBy({left:Number(arrow.dataset.cwSlide)*330,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
    if (pending && event.target.closest('.cart__ctas, cart-drawer-items')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  drawer.addEventListener('change', event => {
    const select = event.target.closest('[data-cw-add] select');
    if (!select) return;
    // CartItems listens for quantity changes; a suggested variant is not a cart line.
    event.stopPropagation();
    const option = select.selectedOptions[0];
    select.form.querySelector('[name="quantity"]').value = option.dataset.min || '1';
    select.form.querySelector('.cw-suggestion-price').textContent = option.dataset.price;
  }, true);
  drawer.addEventListener('submit', async event => {
    const form = event.target.closest('[data-cw-add]');
    if (!form) return;
    event.preventDefault();
    if (pending) return;
    pending = true;
    const body = new FormData(form);
    body.set('sections', drawer.getSectionsToRender().map(section=>section.id).join(','));
    body.set('sections_url', location.pathname);
    const items = drawer.querySelector('cart-drawer-items');
    items?.setAttribute('inert','');
    drawer.setAttribute('aria-busy','true');
    const showError = message => {
      const error = drawer.querySelector('[data-cw-error]') || drawer.querySelector('#CartDrawer-CartErrors');
      if (error) {error.hidden=false;error.textContent=message;}
    };
    let accepted = false;
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/')+'cart/add.js', {method:'POST',body,headers:{Accept:'application/json'}});
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(typeof data.description==='string'?data.description:'Could not add this product. Please try again.');
      accepted = true;
      if (drawer.getSectionsToRender().some(section=>!data.sections?.[section.id])) throw new Error('Item added. Open your cart page to confirm its latest contents.');
      drawer.renderContents(data);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        const publication = publish(PUB_SUB_EVENTS.cartUpdate, {source:'cart-drawer',cartData:data});
        publication?.catch?.(()=>{});
      }
      drawer.querySelector('.drawer__close')?.focus();
    } catch (error) {
      // Read-only recovery: never retry a POST that may already have added an item.
      try {
        const url = new URL(location.href);
        url.searchParams.set('sections',drawer.getSectionsToRender().map(section=>section.id).join(','));
        const recovery = await fetch(url.pathname+url.search,{headers:{Accept:'application/json'},cache:'no-store'});
        const sections = await recovery.json();
        if (!recovery.ok || drawer.getSectionsToRender().some(section=>!sections?.[section.id])) throw new Error('Refresh failed');
        drawer.renderContents({sections});
      } catch (_) {
        // Prevent stale checkout and provide the native cart as recovery.
        const checkout = drawer.querySelector('.cart__ctas');
        if (checkout) { checkout.inert=true; const link=document.createElement('a');link.href=window.routes?.cart_url || '/cart';link.textContent='Open cart to confirm your latest items';checkout.after(link); }
      }
      showError(accepted?'Item added. Please confirm your latest cart before checkout.':error.message);
    } finally {
      pending = false;
      items?.removeAttribute('inert');
      drawer.querySelector('cart-drawer-items')?.removeAttribute('inert');
      drawer.removeAttribute('aria-busy');
    }
  });
})();
